/**
 * 定数型にキャストする
 * @param value 値
 * @param items 定数オブジェクト
 * @returns 定数型 | undefined
 */
export function castConstantType<T>(value: string | number, items: {}): T | undefined {
  for (const val of Object.values(items)) {
    if (val === value) {
      return value as T
    } else if (val === Number(value)) {
      return Number(value) as T
    }
  }
  return undefined
}

/**
 * 性別
 */
export const Gender = {
  Male: 'male',
  Female: 'female',
  Other: 'other',
} as const
export type GenderType = (typeof Gender)[keyof typeof Gender]

/**
 * 事業形態(ユーザータイプ)
 */
export const BusinessForm = {
  Individual: 1,
  Company: 2,
} as const
export type BusinessFormType = (typeof BusinessForm)[keyof typeof BusinessForm]

/**
 * 概要書面タイプ(紙面タイプ)
 */
export const SummaryDocumentKind = {
  Electronic: 1,
  Paper: 2,
} as const
export type SummaryDocumentKindType = (typeof SummaryDocumentKind)[keyof typeof SummaryDocumentKind]

/**
 * ForceClub会員ステータス
 */
export const ForceClubStatus = {
  Registered: 1,
  Member: 2,
  None: 3,
} as const
export type ForceClubStatusType = (typeof ForceClubStatus)[keyof typeof ForceClubStatus]

/**
 * ポジションステータス
 */
export const PositionStatus = {
  SelectPosition: 1,
  SummaryConfirmation: 2,
  ApplicationConfirmation: 3,
  Completed: 4,
} as const
export type PositionStatusType = (typeof PositionStatus)[keyof typeof PositionStatus]

/**
 * 契約ステータス
 */
export const ContractStatus = {
  /** 初回登録 */
  InitialRegistration: 0,
  /** メールアドレス承認済み */
  EmailAddressApproved: 1,
  /** 概要書面交付済み */
  SummaryDocumentDelivered: 2,
  /** お申込み・承諾登録済み */
  ApplicationAndAgreementRegistered: 3,
  /** 個人証明書登録済み */
  PersonalDocumentRegistered: 4,
  /** 法人証明書登録待ち */
  CorporateDocumentRegistrationPending: 5,
  /** 法人証明書登録済み */
  CorporateDocumentRegistered: 6,
  /** 法人証明書否認 */
  CorporateDocumentRejected: 7,
  /** 完了 */
  Completed: 8,
} as const
export type ContractStatusType = (typeof ContractStatus)[keyof typeof ContractStatus]

/**
 * 追認項目の値
 */
export const AgreedStatus = {
  Yes: 0,
  No: 1,
} as const
export type AgreedStatusType = (typeof AgreedStatus)[keyof typeof AgreedStatus]

/**
 * ポジションID
 */
export const PositionNumber = {
  MainPosition: 1,
  SubPosition1: 2,
  SubPosition2: 3,
  SubPosition3: 4,
  SubPosition4: 5,
  SubPosition5: 6,
  SubPosition6: 7,
} as const
export type PositionNumberType = (typeof PositionNumber)[keyof typeof PositionNumber]

/**
 * メインポジションかサブポジションか
 */
export const PositionIsMainOrSub = {
  Main: 1,
  Sub: 2,
} as const
export type PositionIsMainOrSubType = (typeof PositionIsMainOrSub)[keyof typeof PositionIsMainOrSub]

/**
 * 名義変更ステータス
 */
export const AssignmentChangeStatus = {
  /** 未設定 */
  Unset: 0,
  /** 名義変更済み */
  Changed: 1,
  /** 名義変更依頼中 */
  ChangePending: 2,
} as const
export type AssignmentChangeStatusType = (typeof AssignmentChangeStatus)[keyof typeof AssignmentChangeStatus]

/**
 * 支払いステータス
 */
export const PaymentStatus = {
  Unpaid: 0,
  Paid: 1,
} as const
export type PaymentStatusType = (typeof PaymentStatus)[keyof typeof PaymentStatus]

/**
 * 解約種別
 */
export const Cancellation = {
  /** 契約中 */
  Contract: 0,
  /** 解約 */
  Unsubscribe: 1,
  /** クーリングオフ処理中 */
  CoolingOffPending: 2,
  /** クーリングオフ処理済み */
  CoolingOff: 3,
} as const
export type CancellationType = (typeof Cancellation)[keyof typeof Cancellation]

/**
 * 決済方法
 * 1: 銀行振込, 2: 銀行振替, 3: クレジットカード
 */
export const PaymentMethod = {
  BankTransfer: 1,
  CreditTransfer: 2,
  CreditCard: 3,
} as const
export type PaymentMethodType = (typeof PaymentMethod)[keyof typeof PaymentMethod]

/**
 * 預金種目
 */
export const BankAccount = {
  /** 普通 */
  Normal: 'normal',
  /** 当座 */
  Current: 'current',
} as const
export type BankAccountType = (typeof BankAccount)[keyof typeof BankAccount]

/**
 * バイナリタイプ
 */
export const Binary = {
  None: 0,
  Left: 1,
  Right: 2,
} as const
export type BinaryType = (typeof Binary)[keyof typeof Binary]

/**
 * 概要書面有効性
 * 0: 有効, 1: 使用済み, 2: 無効
 */
export const SummaryDocumentStatus = {
  Valid: 0,
  Used: 1,
  Invalid: 2,
} as const
export type SummaryDocumentStatusType = (typeof SummaryDocumentStatus)[keyof typeof SummaryDocumentStatus]

/**
 * お問い合わせ種別
 *　1: ログイン方法について（パスワード失念等）
 *　2: 登録方法について
 *　3: 決済について
 *　4: 登録内容変更について
 *　5: その他
 *　6: ATEMクエスト新名称予想キャンペーン
 */
export const ContactCategory = {
  Login: 1,
  Registration: 2,
  Settlement: 3,
  ChangeRegistration: 4,
  Other: 5,
  Campaign: 6,
} as const
export type ContactCategoryType = (typeof ContactCategory)[keyof typeof ContactCategory]

/**
 * 口座ステータス
 * unused: 未使用
 * complete: 入金済
 * shortage: 金額不足
 * excess: 金額超過
 */
export const AccountStatus = {
  Unused: 'unused',
  Complete: 'complete',
  Shortage: 'shortage',
  Excess: 'excess',
} as const
export type AccountStatusType = (typeof AccountStatus)[keyof typeof AccountStatus]

/**
 * 証明書種別
 */
export const License = {
  /** 運転免許証 */
  DriversLicense: 1,
  /** マイナンバーカード */
  IndividualNumberCard: 2,
  /** パスポート */
  Passport: 3,
  /** 在住カード */
  ResidenceCard: 4,
} as const
export type LicenseType = (typeof License)[keyof typeof License]

/**
 * 個人契約の証明書検証結果
 */
export const IndividualLicenseCheck = {
  /** 有効 */
  Valid: 'valid',
  /** TRUSTDOCK側で拒否 */
  TrustDockInvalid: 'trustdock_invalid',
  /** 名前が無効 */
  NameInvalid: 'name_invalid',
  /** 誕生日が無効 */
  BirthdayInvalid: 'birthday_invalid',
  /** 証明書が期限切れ */
  Expired: 'expired',
  /** 証明書画像が無効 */
  ImageInvalid: 'image_invalid',
  /** 証明書登録不可（ステータスが適切でない） */
  NotAllowed: 'not_allowed',
  /** TrustDock APIエラー */
  TrustDockError: 'trustdock_error',
  /** BPO審査中 */
  BpoReview: 'bpo_review',
} as const
export type IndividualLicenseCheckType = (typeof IndividualLicenseCheck)[keyof typeof IndividualLicenseCheck]

/**
 * コネクター登録種別
 * invite: 招待
 * assignment: 名義変更
 */
export const RegisterKind = {
  Invite: 'invite',
  Assignment: 'assignment',
} as const
export type RegisterKindType = (typeof RegisterKind)[keyof typeof RegisterKind]

/**
 * ユーザー区分
 */
export const UserClassification = {
  /** 一般ユーザー */
  General: 'general',
  /** 名義変更ユーザー */
  GeneralChangeAssignment: 'general_change_assignment',
  /** 事前登録ユーザー */
  AdvancedRegistration: 'advanced_registration',
  /** 事前名義変更ユーザー */
  AdvancedRegistrationChangeAssignment: 'advanced_registration_change_assignment',
} as const
export type UserClassificationType = (typeof UserClassification)[keyof typeof UserClassification]

/**
 * 支払種別
 */
export const PaymentService = {
  Credix: 'credix',
  PanaceaPay: 'panacea-pay',
} as const
export type PaymentServiceType = (typeof PaymentService)[keyof typeof PaymentService]

/**
 * 報酬明細取込進捗状況
 */
export const RewardSlipImportProgressStatus = {
  Waiting: 'waiting',
  Running: 'running',
  Completed: 'completed',
  Failed: 'failed',
} as const
export type RewardSlipImportProgressStatusType = (typeof RewardSlipImportProgressStatus)[keyof typeof RewardSlipImportProgressStatus]

/**
 * 入金アラート支払ステータス
 * 1: 入金不足
 * 2: 過剰入金
 * 3: 配置未選択
 */
export const PaymentStatusForPaymentAlert = {
  shortage: 1,
  excess: 2,
  binaryPlacementMissing: 3,
} as const
export type PaymentStatusForPaymentAlertType = (typeof PaymentStatusForPaymentAlert)[keyof typeof PaymentStatusForPaymentAlert]
